import { LoginResponse } from '@/actions/auth/sign-in'
import { AwardPayload } from '@/app/awards/edit/[id]/CreateEditAwardForm'
import {
	FinalistSearchPayload,
	FinalistSearchResultDto,
	MarkEntriesAsFinalistDto,
	RevealResultsEntrySearchDto,
} from '@/app/entries/finalists/types'
import {
	CreateJudgePayload,
	EntryNoteDto,
	EntryProfileDto,
	EntryProfileResponseDto,
	ExtendPhaseDeadlinePayload,
	JudgingPhaseFormElementDto,
	LinkEntryCompanyPayloadType,
	LinkEntryContactPayloadType,
	UnsubmitPhasePayload,
	UpdateAccessibilityPayload,
	YearJudgeDto,
} from '@/app/entries/profile/[id]/types'
import { FinalistEntrySearchQueryDto } from '@/app/entries/reveal/types'
import { EntriesSearchPayloadType, EntriesSearchResult } from '@/app/entries/search/types'
import { EntriesContactsAssetsUploadPayload } from '@/app/entries/uploadAssets/types'
import { EoiReportState } from '@/app/reports/summary-reports/EoiReportTab'
import {
	JudgingConflictsFormDataDto,
	ReportRequestDto,
	ReportSummaryDataRequestDto,
	ReportSummaryDto,
} from '@/app/reports/summary-reports/types'
import { AssetDto, UrlDto } from '@/types/assets'
import { AdminCurrentUserDto } from '@/types/auth'
import { ListFormElementDto, PaginatedResult, RedirectDto } from '@/types/baseTypes'
import { CompanyDto } from '@/types/companies'
import {
	ContactDto,
	ContactJudgingNoteDto,
	ContactJudgingStatusLabelDto,
	ContactJudgingStatusValueDto,
	ContactSearchDto,
	NoteDto,
} from '@/types/contacts'
import { EmployeeChangePassword, EmployeeDto } from '@/types/employees'
import { EnquiryStatusDto } from '@/types/enquiries'
import {
	LinkNominationNominatorContactPayload,
	LinkNominationNomineeCompanyPayload,
	LinkNominationNomineeContactPayload,
	NominationContactDto,
	NominationDeclinedReasonUpdateDto,
	NominationNoteDto,
	UpdateVirtualEventPayloadType,
} from '@/types/nominations'
import ApiResponseError, { ApiError, FieldError } from '@/utils/ApiResponseError'
import axios, { AxiosError } from 'axios'

const baseURL = '/api'

console.log(`Configuring baseURL: ${baseURL}`)

export const apiClient = axios.create({
	method: 'GET',
	withCredentials: true,
	baseURL,
	transformResponse: [
		data => {
			try {
				return JSON.parse(data)
			} catch (error) {
				return {}
			}
		},
	],
})

apiClient.interceptors.request.use(
	async config => {
		// console.log('onRequest', config.headers);
		// TODO: modify backend to just set a cookie
		// const accessToken = await getTokenAction();
		config.headers['Content-Type'] = 'application/json'
		config.headers['Cache-Control'] = 'no-cache'
		// if (accessToken) {
		//   config.headers["X-Token"] = `Bearer ${accessToken}`;
		// }
		return config
	},
	error => {
		return Promise.reject(error)
	},
)

apiClient.interceptors.response.use(
	response => response.data,
	async (error: Error | AxiosError<ApiError>) => {
		if (axios.isAxiosError(error)) {
			if (error.response?.status === 401) {
				window.location.href = '/auth/login'
				throw Error('Invalid token, redirect to login')
			} else {
				if (error.response?.data !== undefined) {
					throw new ApiResponseError(
						error.response?.data as {
							message: string
							title: string
							redirect: boolean
							fieldErrors: FieldError[]
						},
					)
				}
				throw new ApiResponseError({
					message: 'unknown error occurred',
					title: '',
					redirect: false,
					fieldErrors: [],
				})
			}
		}
		throw error
	},
)

export const apiClientMultiPart = axios.create({
	method: 'GET',
	withCredentials: true,
	baseURL,
	transformResponse: [
		data => {
			try {
				return JSON.parse(data)
			} catch (error) {
				return {}
			}
		},
	],
})

apiClientMultiPart.interceptors.request.use(
	async config => {
		// console.log('onRequest', config.headers);
		// const accessToken = await getTokenAction()
		config.headers['Cache-Control'] = 'no-cache'
		// TODO: make backend set a cookie instead of returnign token
		// if (accessToken) {
		// 	config.headers['X-Token'] = `Bearer ${accessToken}`
		// }
		return config
	},
	error => {
		return Promise.reject(error)
	},
)

apiClientMultiPart.interceptors.response.use(
	response => response.data,
	async (error: Error | AxiosError<ApiError>) => {
		if (axios.isAxiosError(error)) {
			if (error.response?.status === 401) {
				window.location.href = '/auth/login'
				throw Error('Invalid token, redirect to login')
			} else {
				if (error.response?.data !== undefined) {
					throw new ApiResponseError(error.response?.data as ApiError)
				}
				throw new ApiResponseError({
					message: 'unknown error occurred',
					title: '',
					redirect: false,
					fieldErrors: [],
				})
			}
		}
		throw error
	},
)

// Contacts endpoints
export async function contactSearch(payload: any): Promise<PaginatedResult<ContactSearchDto>> {
	return apiClient(`/contacts/search`, {
		method: 'POST',
		data: payload,
	})
}
export async function exportContacts(payload: any): Promise<any> {
	return apiClient(`/contacts/export`, {
		method: 'POST',
		data: payload,
	})
}

export async function login(email: string, password: string): Promise<LoginResponse> {
	return apiClient(`/employees/login`, {
		method: 'POST',
		data: { email, password },
	})
}

export async function loginOTP(credentials: Partial<Record<'otpCode' | 'email' | 'password', unknown>>): Promise<any> {
	return apiClient(`/employees/otplogin`, {
		method: 'POST',
		data: { ...credentials },
	})
}

export async function currentUser(): Promise<AdminCurrentUserDto> {
	return apiClient(`/employees/currentuser`, {
		method: 'GET',
	})
}

export async function resendEmployeeLink(employeeId: string): Promise<any> {
	return apiClient(`/employees/${employeeId}/resendemail`, {
		method: 'PUT',
	})
}

export async function unlockEmployee(employeeId: string): Promise<any> {
	return apiClient(`/employees/${employeeId}/unlock`, {
		method: 'PUT',
	})
}

export async function deleteEmployee(employeeId: string): Promise<any> {
	return apiClient(`/employees/${employeeId}`, {
		method: 'DELETE',
	})
}

export async function createEmployee(payload: EmployeeDto): Promise<EmployeeDto> {
	return apiClient(`/employees`, {
		method: 'POST',
		data: payload,
	})
}

export async function updateEmployee(employeeId: string, payload: EmployeeDto, version: number): Promise<EmployeeDto> {
	return apiClient(`/employees/${employeeId}`, {
		method: 'PUT',
		data: payload,
		headers: {
			'If-Match': `${version}`,
		},
	})
}

export async function changeEmployeePassword(payload: EmployeeChangePassword): Promise<void> {
	return apiClient(`/employees/changepassword`, {
		method: 'PUT',
		data: payload,
	})
}

export async function contactSearchForm(): Promise<any> {
	return apiClient(`/contacts/search/form`, {
		method: 'GET',
	})
}

export async function createContact(payload: ContactDto): Promise<any> {
	return apiClient(`/contacts`, {
		method: 'POST',
		data: payload,
	})
}

export async function updateContact(payload: ContactDto, version: number): Promise<any> {
	return apiClient(`/contacts/${payload.id}`, {
		method: 'PUT',
		data: payload,
		headers: {
			'If-Match': `${version}`,
		},
	})
}

export async function deleteContact(contactId: string): Promise<void> {
	return apiClient(`/contacts/${contactId}`, {
		method: 'DELETE',
	})
}

export async function resendEmail(contactId: string, emailType: string): Promise<void> {
	return apiClient(`/contacts/${contactId}/sendemail`, {
		method: 'POST',
		data: { template: emailType },
	})
}

export async function unlockContact(contactId: string): Promise<void> {
	return apiClient(`/contacts/${contactId}/unlock`, {
		method: 'PUT',
	})
}

export async function loginOnBehalf(contactId: string, program: string): Promise<RedirectDto> {
	return apiClient(`/contacts/${contactId}/loginonbehalf/${program}`, {
		method: 'PUT',
	})
}

export async function loginOnBehalfOfJudge(contactId: string): Promise<RedirectDto> {
	return apiClient(`/judges/${contactId}/loginonbehalf`, {
		method: 'PUT',
	})
}

export async function addContactNote(contactId: string, comment: string): Promise<NoteDto> {
	return apiClient(`/contacts/${contactId}/note`, {
		method: 'POST',
		data: { comment },
	})
}

export async function createJudge(
	contactId: string,
	payload: ContactJudgingStatusValueDto,
): Promise<ContactJudgingStatusLabelDto> {
	return apiClient(`/contacts/${contactId}/judges`, {
		method: 'POST',
		data: payload,
	})
}

export async function updateJudge(
	id: string,
	payload: ContactJudgingStatusValueDto,
	version: number,
): Promise<ContactJudgingStatusLabelDto> {
	return apiClient(`/contacts/judges/${id}`, {
		method: 'PUT',
		data: payload,
		headers: {
			'If-Match': `${version}`,
		},
	})
}

export async function deleteJudge(judgeId: string): Promise<void> {
	return apiClient(`/contacts/judges/${judgeId}`, {
		method: 'DELETE',
	})
}

export async function createJudgeNote(
	contactId: string,
	payload: ContactJudgingNoteDto,
): Promise<ContactJudgingNoteDto> {
	return apiClient(`/contacts/${contactId}/judgesnote`, {
		method: 'POST',
		data: payload,
	})
}

export async function updateJudgeNote(
	id: string,
	payload: ContactJudgingNoteDto,
	version: number,
): Promise<ContactJudgingNoteDto> {
	console.log('updating version', version)
	return apiClient(`/contacts/judgesnote/${id}`, {
		method: 'PUT',
		data: payload,
		headers: {
			'If-Match': `${version}`,
		},
	})
}

export async function deleteJudgeNote(noteId: string): Promise<void> {
	return apiClient(`/contacts/judgesnote/${noteId}`, {
		method: 'DELETE',
	})
}

export async function addAsset(payload: FormData): Promise<AssetDto> {
	return apiClientMultiPart(`/asset`, {
		method: 'POST',
		data: payload,
	})
}

export async function downloadAsset(assetId: string): Promise<UrlDto> {
	return apiClient(`/asset/${assetId}`, {
		method: 'GET',
	})
}

export async function removeAsset(contactId: string, assetId: string): Promise<void> {
	return apiClient(`/contacts/${contactId}/asset/${assetId}`, {
		method: 'PUT',
	})
}

export async function createCompany(payload: CompanyDto): Promise<any> {
	return apiClient(`/companies`, {
		method: 'POST',
		data: payload,
	})
}

export async function updateCompany(payload: CompanyDto, version: number): Promise<any> {
	return apiClient(`/companies/${payload.id}`, {
		method: 'PUT',
		data: payload,
		headers: {
			'If-Match': `${version}`,
		},
	})
}

export async function searchCompany(keyword: string, offset: number, limit: number): Promise<any> {
	return apiClient(`/companies/search?keyword=${keyword}&offset=${offset}&limit=${limit}`, {
		method: 'GET',
	})
}

// Nomination search endpoints
export async function nominationSearch(payload: any): Promise<any> {
	return apiClient(`/nominations/search`, {
		method: 'POST',
		data: payload,
	})
}
export async function nominationExport(payload: any): Promise<any> {
	return apiClient(`/nominations/export`, {
		method: 'POST',
		data: payload,
	})
}

export async function nominationUpdateNominator(
	id: string,
	payload: LinkNominationNominatorContactPayload,
	version: number,
): Promise<NominationContactDto> {
	console.log('nominationSetNominator', id, payload, version)
	return apiClient(`/nominations/${id}/nominator`, {
		method: 'PUT',
		data: payload,
		headers: {
			'If-Match': `${version}`,
		},
	})
}

export async function nominationUpdateNomineeContact(
	id: string,
	payload: LinkNominationNomineeContactPayload,
	version: number,
): Promise<NominationContactDto> {
	console.log('nominationSetNominator', id, payload, version)
	return apiClient(`/nominations/${id}/contact`, {
		method: 'PUT',
		data: payload,
		headers: {
			'If-Match': `${version}`,
		},
	})
}

export async function nominationUpdateNomineeCompany(
	id: string,
	payload: LinkNominationNomineeCompanyPayload,
	version: number,
): Promise<NominationContactDto> {
	console.log('nominationSetNominator', id, payload, version)
	return apiClient(`/nominations/${id}/company`, {
		method: 'PUT',
		data: payload,
		headers: {
			'If-Match': `${version}`,
		},
	})
}

export async function updateNominatorAnonymous(
	id: string,
	payload: {
		isAnonymous: boolean
		nominationId: string
	},
	version: number,
): Promise<any> {
	return apiClient(`/nominations/${id}/anonymous`, {
		method: 'PUT',
		data: payload,
		headers: {
			'If-Match': `${version}`,
		},
	})
}

export async function getNominationDeclinedReasons(id: string): Promise<ListFormElementDto> {
	return apiClient(`/nominations/declinedreasons/${id}`, {
		method: 'GET',
	})
}

export async function updateNominationDeclinedReasons(
	id: string,
	payload: NominationDeclinedReasonUpdateDto,
	version: number,
) {
	return apiClient(`/nominations/${id}/declinedreasons`, {
		data: payload,
		method: 'PUT',
		headers: {
			'If-Match': `${version}`,
		},
	})
}

export async function updateVirtualEventTracking(id: string, payload: UpdateVirtualEventPayloadType, version: number) {
	return apiClient(`/nominations/${id}/virtualevent`, {
		data: payload,
		method: 'PUT',
		headers: {
			'If-Match': `${version}`,
		},
	})
}

export async function nominationSendEmail(id: string, payload: { template: 'Nominee' | 'Nominator' }): Promise<any> {
	return apiClient(`/nominations/${id}/sendemail`, {
		method: 'POST',
		data: payload,
	})
}

export async function addNominationNote(nominationId: string, comment: string): Promise<NominationNoteDto> {
	return apiClient(`/nominations/${nominationId}/note`, {
		method: 'POST',
		data: { comment },
	})
}

// Entries
export async function entriesSearch(payload: EntriesSearchPayloadType): Promise<EntriesSearchResult> {
	return apiClient(`/entries/search`, {
		method: 'POST',
		data: payload,
	})
}

export async function entriesSearchDownloadPdfsBulk(payload: EntriesSearchPayloadType): Promise<UrlDto> {
	return apiClient(`/entries/bulk/pdf`, {
		method: 'POST',
		data: payload,
	})
}

export async function entriesSearchStage3Answers(payload: EntriesSearchPayloadType): Promise<UrlDto> {
	return apiClient(`/entries/bulk/uploads`, {
		method: 'POST',
		data: payload,
	})
}

export async function entriesSearchDownloadBulkDocs(payload: EntriesSearchPayloadType): Promise<UrlDto> {
	return apiClient(`/entries/bulk/docs`, {
		method: 'POST',
		data: payload,
	})
}

export async function entriesSearchDownloadPhotoDocs(payload: EntriesSearchPayloadType): Promise<UrlDto> {
	return apiClient(`/entries/bulk/photos`, {
		method: 'POST',
		data: payload,
	})
}

export async function entriesSearchDownloadLogoDocs(payload: EntriesSearchPayloadType): Promise<UrlDto> {
	return apiClient(`/entries/bulk/logos`, {
		method: 'POST',
		data: payload,
	})
}

export async function entriesSearchExport(payload: EntriesSearchPayloadType): Promise<any> {
	return apiClient(`/entries/export`, {
		method: 'POST',
		data: payload,
	})
}

export async function entriesDownloadPdf(entryId: string): Promise<{ signedFileUrl: string }> {
	return apiClient(`/entries/${entryId}/pdf`, {
		method: 'GET',
	})
}

export async function entriesDownloadDoc(entryId: string): Promise<{ signedFileUrl: string }> {
	return apiClient(`/entries/${entryId}/docs`, {
		method: 'GET',
	})
}

export async function entriesSearchContactsAssetsUpload(payload: EntriesContactsAssetsUploadPayload): Promise<{
	rows: FinalistSearchResultDto[]
}> {
	return apiClient(`/entries/multiasset/search`, {
		method: 'POST',
		data: payload,
	})
}

export async function addEntryAsset(payload: FormData): Promise<any> {
	return apiClientMultiPart(`/entries/multiasset`, {
		method: 'POST',
		data: payload,
	})
}

export async function getEntryProfile(entryId: string): Promise<EntryProfileDto> {
	return apiClient(`/entries/${entryId}`, {
		method: 'GET',
	})
}

export async function updateAccessibilityNeeds(
	entryId: string,
	payload: UpdateAccessibilityPayload,
): Promise<EntryProfileResponseDto> {
	return apiClient(`/entries/${entryId}/accessibility`, {
		method: 'PUT',
		data: payload,
	})
}

export async function unsubmitPhase(entryId: string, payload: UnsubmitPhasePayload): Promise<EntryProfileResponseDto> {
	return apiClient(`/entries/${entryId}/unsubmit`, {
		method: 'PUT',
		data: payload,
	})
}

export async function extendDeadline(
	entryId: string,
	payload: ExtendPhaseDeadlinePayload,
): Promise<EntryProfileResponseDto> {
	return apiClient(`/entries/${entryId}/extendeddeadline`, {
		method: 'PUT',
		data: payload,
	})
}

export async function updateEntryContact(id: string, payload: LinkEntryContactPayloadType): Promise<any> {
	return apiClient(`/entries/${id}/contact`, {
		method: 'PUT',
		data: payload,
	})
}

export async function updateEntryCompany(id: string, payload: LinkEntryCompanyPayloadType): Promise<any> {
	return apiClient(`/entries/${id}/company`, {
		method: 'PUT',
		data: payload,
	})
}

export async function entrySendEmail(id: string, payload: { template: string }): Promise<any> {
	return apiClient(`/entries/${id}/sendemail`, {
		method: 'POST',
		data: payload,
	})
}

export async function addEntryNote(entryId: string, comment: string): Promise<EntryNoteDto> {
	return apiClient(`/entries/${entryId}/note`, {
		method: 'POST',
		data: { comment },
	})
}

export async function getEntryStageCategory(entryId: string): Promise<JudgingPhaseFormElementDto[]> {
	return apiClient(`/entries/judges/form/${entryId}`, {
		method: 'GET',
	})
}

export async function getEntryAvailableJudges(payload: {
	entryId: string
	yearCategoryId: string
	judgingPhase: string
}): Promise<YearJudgeDto[]> {
	return apiClient(`/entries/judges/search`, {
		method: 'POST',
		data: payload,
	})
}

export async function updateEntryJudges(entryId: string, payload: CreateJudgePayload): Promise<YearJudgeDto[]> {
	return apiClient(`/entries/judges/update/${entryId}`, {
		method: 'PUT',
		data: payload,
	})
}

export async function deleteEntryJudge(
	entryId: string,
	yearCategoryId: string,
	judgingPhase: string,
): Promise<YearJudgeDto[]> {
	return apiClient(`/entries/judges/remove/${entryId}/${yearCategoryId}/${judgingPhase}`, {
		method: 'DELETE',
	})
}

export async function finalistsSearch(payload: FinalistSearchPayload): Promise<{
	rows: RevealResultsEntrySearchDto[]
}> {
	return apiClient(`/entries/finalists/search`, {
		method: 'POST',
		data: payload,
	})
}

export async function markAsFinalists(payload: MarkEntriesAsFinalistDto): Promise<void> {
	return apiClient(`/entries/finalists`, {
		method: 'POST',
		data: payload,
	})
}

export async function revealResultsSearch(payload: FinalistEntrySearchQueryDto): Promise<{
	rows: RevealResultsEntrySearchDto[]
}> {
	return apiClient(`/entries/revealresults/search`, {
		method: 'POST',
		data: payload,
	})
}

export async function revealResults(payload: FinalistEntrySearchQueryDto): Promise<void> {
	return apiClient(`/entries/revealresults`, {
		method: 'POST',
		data: payload,
	})
}

export async function hideResults(payload: FinalistEntrySearchQueryDto): Promise<void> {
	return apiClient(`/entries/hideresults`, {
		method: 'POST',
		data: payload,
	})
}

export async function downloadBHC(id: string): Promise<UrlDto> {
	return apiClient(`/entries/${id}/businesshealthcheck`, {
		method: 'GET',
	})
}

export async function downloadDocs(id: string): Promise<UrlDto> {
	return apiClient(`/entries/${id}/docs`, {
		method: 'GET',
	})
}

export async function downloadPDF(id: string): Promise<UrlDto> {
	return apiClient(`/entries/${id}/pdf`, {
		method: 'GET',
	})
}

export async function loginOnBehalfEntry(id: string): Promise<RedirectDto> {
	return apiClient(`/entries/${id}/loginonbehalf`, {
		method: 'PUT',
	})
}

// Awards / Years
export async function updateYear(id: string, payload: AwardPayload, version: number): Promise<NominationContactDto> {
	return apiClient(`/years/${id}`, {
		method: 'PUT',
		data: payload,
		headers: {
			'If-Match': `${version}`,
		},
	})
}

export async function createYear(payload: AwardPayload): Promise<NominationContactDto> {
	return apiClient(`/years`, {
		method: 'POST',
		data: payload,
	})
}

// Enquiry search endpoints
export async function enquirySearch(payload: any): Promise<any> {
	return apiClient(`/enquiries/search`, {
		method: 'POST',
		data: payload,
	})
}

export async function updateEnquiryStatus(enquiryId: string, payload: EnquiryStatusDto): Promise<any> {
	return apiClient(`/enquiries/${enquiryId}`, {
		method: 'PUT',
		data: payload,
	})
}

export async function addEnquiryNote(enquiryId: string, comment: string): Promise<NoteDto> {
	return apiClient(`/enquiries/${enquiryId}/note`, {
		method: 'POST',
		data: { comment },
	})
}

// Judging Endpoints
export async function judgingConflictsSearch(payload: any): Promise<any> {
	return apiClient(`/judges/conflicts/search`, {
		method: 'POST',
		data: payload,
	})
}

export async function judgingConflictsExport(payload: any): Promise<any> {
	return apiClient(`/judges/conflicts/export`, {
		method: 'POST',
		data: payload,
	})
}

export async function judgingEntriesReport(payload: any): Promise<any> {
	return apiClient(`/judges/entries/export`, {
		method: 'POST',
		data: payload,
	})
}

export async function judgesReport(payload: any): Promise<any> {
	return apiClient(`/judges/export`, {
		method: 'POST',
		data: payload,
	})
}

export async function judgingConflictsReset(entryId: string): Promise<any> {
	return apiClient(`/judges/conflicts/${entryId}/reset`, {
		method: 'PUT',
	})
}

// Reports
export async function getRequestReportsList(): Promise<ReportRequestDto[]> {
	return apiClient(`/reportrequests`, {
		method: 'GET',
	})
}

export async function getEOIReportFormData(): Promise<JudgingConflictsFormDataDto> {
	return apiClient(`/judges/conflicts/form`, {
		method: 'GET',
	})
}

export async function downloadRequestReport(reportId: string): Promise<UrlDto> {
	return apiClient(`/reportrequests/${reportId}/download`, {
		method: 'POST',
	})
}

export async function exportEOIReport(payload: EoiReportState): Promise<UrlDto> {
	return apiClient(`/reports/eoi`, {
		method: 'POST',
		data: payload,
	})
}

export async function summaryReport(payload: ReportSummaryDataRequestDto): Promise<ReportSummaryDto> {
	return apiClient(`/reports/summary`, {
		method: 'POST',
		data: payload,
	})
}

// Auth
export async function resendOTP(email: string): Promise<void> {
	return apiClient(`/employees/resend/otpemail`, {
		method: 'POST',
		data: { email },
	})
}

export async function allocateJudges(): Promise<void> {
	return apiClient(`/judges/allocate`, {
		method: 'POST',
	})
}

export async function allocateStage4Judges(): Promise<void> {
	return apiClient(`/judges/allocate-stage4`, {
		method: 'POST',
	})
}
